import { useRef, useState } from "react";
import { useForm } from "react-hook-form";
import { useSubmit as useFormspreeSubmit } from "@formspree/react";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { motion, type Variants } from "framer-motion";
import { Link } from "wouter";
import {
  Phone,
  Mail,
  MapPin,
  ArrowRight,
  CalendarDays,
  UsersRound,
  ThumbsUp,
  Award,
  Zap,
  ShieldCheck,
  CheckCircle2,
  AlertCircle,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Card, CardContent } from "@/components/ui/card";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import GoogleMapConsent from "@/components/GoogleMapConsent";
import WhatsAppLink from "@/components/WhatsAppLink";
import { faqItems } from "@/lib/faq";

import heroImg from '@assets/optimized/hero-firmengebaeude.webp';
import heroLogo from '@assets/logo_clean_hq.png';
import meisterbetriebSiegel from '@assets/meisterbetrieb-siegel.webp';
import dekraSiegel from '@assets/Dekra-Siegel.jpg';
import logoHoermann from '@assets/image_1776280916290.png';
import logoAbus from '@assets/image_1776280941516.png';
import teamImg from '@assets/Neuer über uns.jpeg';
import serviceTore from '@assets/leistungen/garagentore/garagentore.jpg';
import serviceBrandschutz from '@assets/leistungen/brandschutz/tueren.jpeg';
import serviceEinbruchschutz from '@assets/leistungen/einbruchschutz/fenstergitter.jpeg';
import serviceMetallbauGelaender from '@assets/leistungen/metallbau/gelaender-mit-bus.webp';

const contactFormSchema = z.object({
  name: z.string().min(2, "Bitte geben Sie Ihren Namen ein."),
  email: z.string().email("Bitte geben Sie eine gültige E-Mail-Adresse ein."),
  telefon: z.string().optional(),
  strasse: z.string().optional(),
  plz: z.string().optional(),
  ort: z.string().optional(),
  message: z.string().min(10, "Bitte geben Sie eine Nachricht ein."),
});

const services = [
  { title: "Tore", desc: "Verkauf, Wartung und Reparatur von Garagen- und Industrietoren", img: serviceTore, route: "/leistungen" },
  { title: "Türen", desc: "Verkauf, Wartung und Reparatur von Türen und Sicherheitslösungen", img: serviceBrandschutz, route: "/leistungen" },
  { title: "Einbruchschutz", desc: "Mechanische Zusatzsicherungen von ABUS und Fenstergitter", img: serviceEinbruchschutz, route: "/einbruchschutz" },
  { title: "Metallbauarbeiten", desc: "Individuelle Metallbauarbeiten, auch aus Edelstahl V2A", img: serviceMetallbauGelaender, route: "/metallbauarbeiten" },
];

const fadeUpVariant = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
} satisfies Variants;

export default function HomePage() {
  const fsSubmit = useFormspreeSubmit<z.infer<typeof contactFormSchema>>("mnjwbdaa");
  const [submissionState, setSubmissionState] = useState({
    submitting: false,
    succeeded: false,
    hasError: false,
  });
  const submissionInProgressRef = useRef(false);
  const successEventPushedRef = useRef(false);

  const form = useForm<z.infer<typeof contactFormSchema>>({
    resolver: zodResolver(contactFormSchema),
    defaultValues: { name: "", email: "", telefon: "", strasse: "", plz: "", ort: "", message: new URLSearchParams(window.location.search).get("anfrage") ? `Anfragegrund: ${new URLSearchParams(window.location.search).get("anfrage")}\n\n` : "" },
  });

  async function onSubmit(data: z.infer<typeof contactFormSchema>) {
    if (submissionInProgressRef.current || submissionState.succeeded) return;

    submissionInProgressRef.current = true;
    setSubmissionState({ submitting: true, succeeded: false, hasError: false });

    try {
      const result = await fsSubmit(data);

      if (result.kind !== "success") {
        submissionInProgressRef.current = false;
        setSubmissionState({ submitting: false, succeeded: false, hasError: true });
        return;
      }

      if (!successEventPushedRef.current) {
        successEventPushedRef.current = true;
        const dataLayerWindow = window as Window & { dataLayer?: unknown[] };
        dataLayerWindow.dataLayer = dataLayerWindow.dataLayer || [];
        dataLayerWindow.dataLayer.push({
          event: "lead_form_success",
          form_name: "kontaktformular",
          lead_type: "kontaktanfrage",
        });
      }

      setSubmissionState({ submitting: false, succeeded: true, hasError: false });
    } catch {
      submissionInProgressRef.current = false;
      setSubmissionState({ submitting: false, succeeded: false, hasError: true });
    }
  }

  const scrollTo = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  return (
    <>
      {/* Hero Section */}
      <section className="relative isolate overflow-visible bg-[#f6f7f7] pb-16 pt-8 md:min-h-[740px] md:pb-28 md:pt-12 lg:min-h-[740px] lg:pt-12">
        <div className="absolute inset-y-0 right-0 z-0 hidden w-[60%] overflow-hidden md:block">
          <img
            src={heroImg}
            alt="Tür- und Torservice Britz Firmengebäude und Fahrzeug"
            className="h-full w-full object-cover object-center opacity-80 grayscale saturate-0"
            style={{ transform: "translateX(15%) scale(1.0)" }}
          />
          <div className="absolute inset-0 bg-white/15" />
          <div className="absolute inset-0 bg-gradient-to-r from-white via-white/72 to-white/18" />
          <div className="absolute inset-x-0 bottom-0 h-48 bg-gradient-to-t from-[#f6f7f7] to-transparent" />
        </div>

        <div
          className="absolute inset-y-0 left-0 z-0 hidden w-[68%] bg-white md:block"
          style={{ clipPath: "polygon(0 0, 86% 0, 72% 100%, 0 100%)" }}
        />

        {/* Mobile background: hero image with light overlay */}
        <div className="absolute inset-0 z-0 md:hidden">
          <img
            src={heroImg}
            alt=""
            className="h-full w-full object-cover grayscale opacity-55"
            style={{ objectPosition: "55% 80%" }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-white/95 via-white/55 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 h-32 bg-gradient-to-t from-[#f6f7f7] to-transparent" />
        </div>
        <div className="container relative z-10 mx-auto px-5 md:px-8">
          <motion.div
            className="max-w-[920px]"
            initial="hidden"
            animate="visible"
            variants={{
              hidden: { opacity: 0, x: -28 },
              visible: { opacity: 1, x: 0, transition: { duration: 0.65, staggerChildren: 0.12 } },
            }}
          >
            <motion.img
              variants={fadeUpVariant}
              src={heroLogo}
              alt="Tür- und Torservice Britz"
              className="hidden md:block mb-9 w-full max-w-[680px] object-contain lg:max-w-[700px]"
            />


            <motion.h1
              variants={fadeUpVariant}
              className="mb-3 max-w-[920px] text-[clamp(1.95rem,8.2vw,2.25rem)] font-black leading-[1.02] tracking-[-0.025em] text-zinc-950 md:mb-6 md:text-[3rem] md:leading-[1.13] lg:text-[3.2rem] xl:text-[3.35rem]"
            >
              <span className="md:hidden">
                <span className="block whitespace-nowrap">Ihr Metallbau-</span>
                <span className="block whitespace-nowrap">Meisterbetrieb</span>
                <span className="block whitespace-nowrap">für Türen, Tore und</span>
                <span className="block whitespace-nowrap">Sicherheit</span>
              </span>
              <span className="hidden md:inline">
                Ihr Metallbau-Meisterbetrieb für Türen, Tore und Sicherheit
              </span>
            </motion.h1>

            <motion.p
              variants={fadeUpVariant}
              className="mb-4 text-base font-bold leading-tight text-primary md:mb-4 md:text-lg"
            >
              Aus Öhringen. Für die Region.
            </motion.p>

            <motion.div
              variants={fadeUpVariant}
              className="mb-3 hidden flex-wrap items-center gap-4 md:mb-8 md:flex md:gap-5"
            >
              <div className="max-w-[620px] min-w-0 text-primary md:max-w-[540px] lg:max-w-[620px]">
                <p className="mb-1.5 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] md:text-sm">
                  <MapPin className="h-4 w-4 shrink-0" aria-hidden="true" />
                  Einsatzgebiet
                </p>
                <p className="text-lg font-bold leading-snug md:text-xl lg:text-2xl">
                  Öhringen • <Link href="/garagentore/neuenstein" className="underline decoration-primary/40 underline-offset-4 hover:decoration-primary">Neuenstein</Link> • <Link href="/garagentore/kuenzelsau" className="underline decoration-primary/40 underline-offset-4 hover:decoration-primary">Künzelsau</Link> • <Link href="/garagentore/bretzfeld" className="underline decoration-primary/40 underline-offset-4 hover:decoration-primary">Bretzfeld</Link> • <Link href="/garagentore/schwaebisch-hall" className="underline decoration-primary/40 underline-offset-4 hover:decoration-primary">Schwäbisch Hall</Link> • <Link href="/garagentore/heilbronn" className="underline decoration-primary/40 underline-offset-4 hover:decoration-primary">Heilbronn</Link> &amp; Umgebung
                </p>
              </div>
              {/* DEKRA nur auf Desktop inline mit Subheadline */}
              <img
                src={dekraSiegel}
                alt="DEKRA Zertifizierung"
                className="hidden md:block h-auto w-[78px] object-contain lg:w-[94px]"
              />
            </motion.div>

            {/* Mobile Trust-Badge-Leiste: beide Siegel nebeneinander */}
            <motion.div
              variants={fadeUpVariant}
              className="mb-5 flex items-center gap-5 md:hidden"
            >
              <img
                src={meisterbetriebSiegel}
                alt="Meisterbetrieb seit 1990 in Öhringen"
                className="h-auto w-[80px] object-contain drop-shadow-sm"
              />
              <img
                src={dekraSiegel}
                alt="DEKRA Zertifizierung"
                className="h-auto w-[68px] object-contain"
              />
            </motion.div>

            <motion.div variants={fadeUpVariant} className="flex flex-col gap-4 sm:flex-row">
              <Button asChild className="h-14 w-full justify-center rounded-xl px-4 text-base font-bold shadow-[0_16px_28px_rgba(42,143,48,0.23)] sm:w-auto md:hidden">
                <a href="tel:0794133514">
                  <CalendarDays className="mr-2 h-6 w-6" /> Jetzt Termin vereinbaren
                </a>
              </Button>
              <Button asChild className="hidden h-14 w-full justify-center rounded-xl px-4 text-base font-bold shadow-[0_16px_28px_rgba(42,143,48,0.23)] sm:w-auto md:inline-flex md:h-16 md:px-8 md:text-lg">
                <a href="/#kontakt">
                  <CalendarDays className="mr-2 h-6 w-6" /> Jetzt Termin vereinbaren
                </a>
              </Button>
              <Button
                variant="outline"
                className="h-14 w-full justify-center rounded-xl border-2 border-primary bg-white px-4 text-base font-bold text-primary hover:bg-primary/5 sm:w-auto md:h-16 md:px-8 md:text-lg"
                onClick={() => scrollTo("leistungen")}
              >
                Leistungen ansehen <ArrowRight className="ml-3 h-6 w-6" />
              </Button>
            </motion.div>
          </motion.div>
        </div>

        <motion.div
          className="absolute right-[6%] top-[70px] z-20 hidden lg:block"
          initial={{ opacity: 0, y: -16, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.55, delay: 0.25 }}
        >
          <img
            src={meisterbetriebSiegel}
            alt="Meisterbetrieb seit 1990 in Öhringen"
            className="h-auto w-[180px] object-contain drop-shadow-md"
          />
        </motion.div>

        <div className="container relative z-30 mx-auto mt-16 px-5 md:mt-10 md:px-8 lg:absolute lg:bottom-[-58px] lg:left-1/2 lg:mt-0 lg:-translate-x-1/2">
          <div className="grid grid-cols-2 overflow-hidden rounded-lg border border-zinc-100 bg-white shadow-[0_18px_42px_rgba(0,0,0,0.13)] lg:grid-cols-4">
            {[
              { icon: <UsersRound className="h-8 w-8 md:h-12 md:w-12" />, strong: "Familienbetrieb", normal: "seit 1990" },
              { icon: <ShieldCheck className="h-8 w-8 md:h-12 md:w-12" />, strong: "Eingetragener\nFachbetrieb", normal: "" },
              { icon: <MapPin className="h-8 w-8 md:h-12 md:w-12" />, strong: "Standort", normal: "Öhringen" },
              { icon: <ThumbsUp className="h-8 w-8 md:h-12 md:w-12" />, strong: "Zuverlässig & persönlich" },
            ].map(({ icon, strong, normal }) => (
              <div
                key={strong}
                className="flex min-h-[90px] items-center gap-3 border-b border-zinc-100 px-4 py-4 text-left text-zinc-950 last:border-b-0 [&:nth-child(odd)]:border-r [&:nth-child(odd)]:border-zinc-100 md:min-h-[124px] md:gap-6 md:border-b-0 md:px-8 md:py-7 lg:border-r lg:border-zinc-200 lg:last:border-r-0 lg:[&:nth-child(odd)]:border-r-0"
              >
                <div className="shrink-0 text-primary">{icon}</div>
                <div className="text-sm leading-tight md:text-xl">
                  <p className="whitespace-pre-line font-black">{strong}</p>
                  {normal && <p className="mt-0.5 font-normal text-zinc-600 md:mt-1">{normal}</p>}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="hidden">
          <div className="grid md:grid-cols-[45fr_55fr] gap-8 md:gap-10 items-center">

            {/* Left: Text */}
            <motion.div
              initial="hidden"
              animate="visible"
              variants={{
                hidden: { opacity: 0, x: -30 },
                visible: { opacity: 1, x: 0, transition: { duration: 0.7, staggerChildren: 0.15 } },
              }}
            >
              <motion.span variants={fadeUpVariant} className="text-primary font-bold text-base md:text-lg tracking-wide mb-3 block">
                Ihr Handwerker aus der Region
              </motion.span>
              <motion.p variants={fadeUpVariant} className="text-4xl md:text-5xl lg:text-[3.4rem] font-bold text-zinc-900 leading-tight mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                Türen, Tore und Einbruchschutz aus Öhringen
              </motion.p>

              {/* Subline */}
              <motion.p variants={fadeUpVariant} className="text-zinc-500 text-base md:text-lg leading-relaxed mb-3 max-w-lg">
                Fachgerechte Lösungen rund um Türen und Tore – von der Montage bis zur Wartung alles aus einer Hand. 
              </motion.p>

              {/* Location badge */}
              <motion.p variants={fadeUpVariant} className="text-primary text-sm font-semibold mb-7">
                📍 Ihr Fachbetrieb in der Region Hohenlohe
              </motion.p>

              {/* CTAs */}
              <motion.div variants={fadeUpVariant} className="flex flex-col gap-3 sm:max-w-sm">
                <Button size="lg" className="text-base px-7 gap-2" asChild>
                  <a href="tel:0794133514">
                    <Phone className="w-4 h-4" /> Jetzt Termin vereinbaren
                  </a>
                </Button>
                <Button size="lg" variant="outline" className="text-base px-7 gap-2 border-primary text-primary hover:bg-primary/5" onClick={() => scrollTo("leistungen")}>
                  Leistungen ansehen <ArrowRight className="w-4 h-4" />
                </Button>
              </motion.div>
            </motion.div>

            {/* Right: Image card */}
            <motion.div
              className="relative"
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.15 }}
            >
              <div className="rounded-xl overflow-hidden shadow-2xl">
                <img
                  src={heroImg}
                  alt="Tür- und Torservice Britz Firmengebäude und Fahrzeug"
                  className="w-full h-[380px] md:h-[520px] object-cover object-center"
                />
              </div>
            </motion.div>

          </div>
        </div>
      </section>

      {/* Trust Bar */}
      <section className="hidden">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            {[
              { icon: <Award className="w-5 h-5 text-primary" />, label: "Zwei Meister im Betrieb" },
              { icon: <ShieldCheck className="w-5 h-5 text-primary" />, label: "Eingetragener Fachbetrieb" },
              { icon: <Zap className="w-5 h-5 text-primary" />, label: "Familienbetrieb seit 1991" },
              { icon: <MapPin className="w-5 h-5 text-primary" />, label: "Standort Öhringen" },
            ].map(({ icon, label }) => (
              <div key={label} className="flex flex-col items-center gap-1.5">
                {icon}
                <span className="text-sm font-medium text-zinc-700 whitespace-pre-line text-center leading-tight">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Leistungen Section */}
      <section id="leistungen" className="scroll-mt-28 py-20 md:scroll-mt-32 md:py-32 bg-zinc-50">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
            <h2 className="text-primary font-bold tracking-wider uppercase text-sm mb-3">Unsere Kernleistungen</h2>
            <h3 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-6">Unsere Leistungen im Überblick</h3>
            <p className="text-zinc-600 text-lg">
              Wir bieten Ihnen zuverlässige Lösungen rund um Tore, Türen, UVV-Prüfungen und Metallbau – alles aus einer Hand.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {services.map((service, index) => (
              <motion.div
                key={index}
                className="group"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                variants={{
                  hidden: { opacity: 0, y: 20 },
                  visible: { opacity: 1, y: 0, transition: { duration: 0.5, delay: index * 0.1 } },
                }}
              >
                <Link href={service.route} className="block h-full">
                  <Card className="h-full border-border/50 hover:border-primary/30 transition-all duration-300 hover:shadow-lg bg-white overflow-hidden cursor-pointer">
                    <div className="w-full h-44 overflow-hidden">
                      <img
                        src={service.img}
                        alt={service.title}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    <CardContent className="p-5">
                      <h4 className="text-base font-bold mb-1.5 text-zinc-900">{service.title}</h4>
                      <p className="text-zinc-500 text-sm leading-relaxed">{service.desc}</p>
                    </CardContent>
                  </Card>
                </Link>
              </motion.div>
            ))}
          </div>

          {/* "Alle Leistungen" Button */}
          <motion.div
            className="mt-12 md:mt-16 text-center"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariant}
          >
            <Link
              href="/leistungen"
              className="inline-flex items-center gap-2 bg-primary text-white font-semibold px-8 py-3.5 rounded-full hover:bg-primary/90 transition-all duration-200 hover:shadow-lg text-base"
            >
              Alle Leistungen ansehen <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Über Uns Section */}
      <section id="ueber-uns" className="scroll-mt-28 py-20 md:scroll-mt-32 md:py-32 bg-white">
        <div className="container mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{ hidden: { opacity: 0, x: -30 }, visible: { opacity: 1, x: 0, transition: { duration: 0.7 } } }}
            >
              <div className="relative rounded-2xl overflow-hidden shadow-xl aspect-square md:aspect-[4/3] lg:aspect-square">
                <img
                  src={teamImg}
                  alt="Das Team von Tür- und Torservice Britz"
                  className="w-full h-full object-cover"
                />
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{ hidden: { opacity: 0, x: 30 }, visible: { opacity: 1, x: 0, transition: { duration: 0.7 } } }}
            >
              <h2 className="text-primary font-bold tracking-wider uppercase text-sm mb-3">Über Uns</h2>
              <h3 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-6">Seit über 35 Jahren für Sie im Einsatz</h3>

              <div className="space-y-6 text-zinc-600 text-lg leading-relaxed mb-10">
                <p>
                  Gegründet wurde die Schlosserei Britz 1990 durch den Metallbaumeister und Schweißfachmann Joachim Britz. Über die Jahre ist aus einem kleinen Handwerksbetrieb ein fester Ansprechpartner für viele Kunden in der Region Hohenlohe geworden.
                </p>
                <p>
                  2002 begann die Spezialisierung auf Verkauf, Wartung und Reparatur von Türen und Toren. 
                </p>
                <p>
                  Ende 2021 verstarb der Inhaber Joachim Britz. Die Ehefrau Brigitte Britz übernahm den Betrieb gemeinsam mit den Kindern Felix und Carina Britz und führt ihn seither unter dem Namen Tür- und Torservice Britz weiter. Felix und Carina Britz sind beide Metallbaumeister und haben den Schweißfachmann absolviert.
                </p>
                <p>
                  Zur Kundschaft gehören neben privaten Kunden ebenso Industriefirmen, Architekten, öffentliche Auftraggeber und Hausverwaltungen.
                </p>
                <p>
                  Der Familienbetrieb ist im Öhringer Teilort Eckartsweiler tätig. Das Team legt Wert auf persönliche Betreuung, individuelle Beratung und höchste Qualität.
                </p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div className="border-l-4 border-primary pl-4">
                  <p className="font-bold text-2xl text-zinc-900 mb-1">1990</p>
                  <p className="text-sm text-zinc-600 font-medium">Gegründet als Familienbetrieb</p>
                </div>
                <div className="border-l-4 border-primary pl-4">
                  <p className="font-bold text-2xl text-zinc-900 mb-1">Meisterbetrieb</p>
                  <p className="text-sm text-zinc-600 font-medium">Höchste handwerkliche Qualität</p>
                </div>
                <div className="border-l-4 border-primary pl-4 sm:col-span-2 mt-2">
                  <p className="font-bold text-2xl text-zinc-900 mb-1">Spezialisierung seit 2002</p>
                  <p className="text-sm text-zinc-600 font-medium">
                    Auf Wartung kraftbetätigter Türen und Tore, Brandschutztüren und -tore, einschließlich Montage und Reparaturen.
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Partner Section */}
      <section className="bg-white pb-14 pt-24 md:pb-20 md:pt-28 border-b border-zinc-100">
        <div className="container mx-auto px-4 md:px-6 text-center">
          <motion.h2
            className="text-xs font-bold tracking-widest uppercase text-primary mb-2"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            Wir setzen auf Qualität von
          </motion.h2>
          <motion.p
            className="text-zinc-500 text-sm mb-10"
            initial={{ opacity: 0, y: 10 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
             
          </motion.p>
          <motion.div
            className="flex flex-wrap justify-center items-center gap-8 md:gap-16"
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: 0.15 }}
          >
            {/* Hörmann */}
            <a
              href="https://www.hoermann.de"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-center px-8 py-5 rounded-xl border border-zinc-200 bg-white hover:border-zinc-300 hover:scale-105 hover:shadow-md transition-all duration-300"
              aria-label="Hörmann – Hersteller-Website"
            >
              <img
                src={logoHoermann}
                alt="Hörmann"
                className="h-10 md:h-12 w-auto object-contain transition-transform duration-300"
              />
            </a>

            {/* ABUS */}
            <a
              href="https://www.abus.com"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center justify-center px-8 py-5 rounded-xl border border-zinc-200 bg-white hover:border-zinc-300 hover:scale-105 hover:shadow-md transition-all duration-300"
              aria-label="ABUS – Hersteller-Website"
            >
              <img
                src={logoAbus}
                alt="ABUS"
                className="h-10 md:h-12 w-auto object-contain mix-blend-multiply transition-transform duration-300"
              />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Meisterbetrieb Section */}
      <section className="hidden">
        <div className="container mx-auto px-4 md:px-6 max-w-3xl">
          <motion.div
            className="text-center mb-10"
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-primary font-bold tracking-wider uppercase text-sm mb-2">Qualifikation</p>
            <h2 className="text-2xl md:text-3xl font-bold text-zinc-900 mb-3">Meisterbetrieb</h2>
            <p className="text-zinc-500 text-sm max-w-lg mx-auto">
              Zwei Meister im Betrieb – fachgerechte Ausführung und höchste Qualität im Handwerk.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[
              { name: "Carina Britz", pdf: "/zertifikate/meisterbrief-carina.pdf" },
              { name: "Felix Britz",  pdf: "/zertifikate/meisterbrief-felix.pdf"  },
            ].map((m, i) => (
              <motion.a
                key={m.name}
                href={m.pdf}
                target="_blank"
                rel="noopener noreferrer"
                className="group block rounded-2xl border border-zinc-200 bg-white hover:border-primary/30 hover:shadow-lg transition-all duration-300 overflow-hidden"
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
              >
                {/* PDF-Vorschau */}
                <div className="relative w-full h-56 bg-zinc-50 overflow-hidden border-b border-zinc-100">
                  <iframe
                    src={`${m.pdf}#toolbar=0&navpanes=0&scrollbar=0&page=1`}
                    className="pointer-events-none absolute top-0 left-0"
                    style={{ border: "none", width: "200%", height: "200%", transform: "scale(0.5)", transformOrigin: "0 0" }}
                    title={`Meisterbrief ${m.name}`}
                  />
                  <div className="absolute inset-0 bg-transparent group-hover:bg-black/5 transition-colors duration-300 flex items-end justify-end p-3">
                    <span className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 bg-white/90 backdrop-blur-sm text-primary text-xs font-semibold px-3 py-1.5 rounded-full shadow">
                      Ansehen
                    </span>
                  </div>
                </div>
                {/* Info */}
                <div className="px-5 py-4">
                  <p className="text-xs font-bold tracking-wider uppercase text-primary mb-0.5">Meisterbrief</p>
                  <p className="text-base font-bold text-zinc-900">{m.name}</p>
                  <p className="text-xs text-zinc-500 mt-0.5">Metallbauer-Handwerk · Handwerkskammer Region Stuttgart</p>
                </div>
              </motion.a>
            ))}
          </div>

          <motion.div
            className="mt-8 text-center"
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.45, delay: 0.2 }}
          >
            <a
              href="/zertifikate"
              className="inline-flex items-center gap-2 bg-primary text-white font-semibold px-7 py-3 rounded-full hover:bg-primary/90 transition-colors text-sm"
            >
              Zertifikate <ArrowRight className="w-4 h-4" />
            </a>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="bg-white py-20 md:py-28">
        <div className="container mx-auto px-4 md:px-6 max-w-4xl">
          <motion.div
            className="text-center mb-12"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariant}
          >
            <h2 className="text-primary font-bold tracking-wider uppercase text-sm mb-3">FAQ</h2>
            <h3 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">Häufig gestellte Fragen</h3>
            <p className="text-zinc-600 text-lg">
              Kurze Antworten auf häufige Fragen zu Türen, Toren, Prüfungen und Sicherheitstechnik.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUpVariant}
          >
            <Accordion type="single" collapsible className="rounded-lg border border-zinc-200 bg-white">
              {faqItems.map((item, index) => (
                <AccordionItem
                  key={item.question}
                  value={`faq-${index + 1}`}
                  className="border-zinc-200 px-5 md:px-7 last:border-b-0"
                >
                  <AccordionTrigger className="py-5 text-left text-base font-bold text-zinc-900 hover:text-primary hover:no-underline md:text-lg">
                    {item.question}
                  </AccordionTrigger>
                  <AccordionContent className="pb-5 text-base leading-relaxed text-zinc-600">
                    {item.answer}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </motion.div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="kontakt" className="scroll-mt-28 bg-gray-50 py-20 md:scroll-mt-32 md:py-32">
        <div className="container mx-auto px-4 md:px-6">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <h2 className="text-primary font-bold tracking-wider uppercase text-sm mb-3">Kontakt</h2>
            <h3 className="text-3xl md:text-4xl font-bold text-zinc-900 mb-4">Kontaktieren Sie uns.</h3>
            <p className="text-zinc-600 text-lg">
              Wir sind gerne für Sie da. Rufen Sie uns an oder schreiben Sie uns eine Nachricht – wir melden uns umgehend bei Ihnen zurück.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
            <motion.div
              className="lg:col-span-2 flex flex-col gap-6"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeUpVariant}
            >
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100 flex flex-col gap-7">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-full text-primary shrink-0">
                    <Phone className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Telefon</p>
                    <p className="font-semibold text-zinc-900 text-lg">07941 33 51 4</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-full text-primary shrink-0">
                    <Mail className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">E-Mail</p>
                    <p className="font-semibold text-zinc-900 text-lg">info@tts-britz.de</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 bg-primary/10 rounded-full text-primary shrink-0">
                    <MapPin className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-1">Adresse</p>
                    <p className="font-semibold text-zinc-900 leading-relaxed">
                      Eichklingenweg 7<br />
                      74613 Öhringen - Eckartsweiler
                    </p>
                  </div>
                </div>

                <div className="border-t border-zinc-100 pt-1">
                  <WhatsAppLink variant="contact" linkLocation="contact_section" />
                  <p className="mt-2 px-2 text-center text-xs leading-relaxed text-zinc-500">
                    Mit Klick auf den WhatsApp-Button verlassen Sie unsere Website und werden zu
                    WhatsApp weitergeleitet.
                  </p>
                </div>
              </div>
            </motion.div>

            <motion.div
              id="kontaktformular"
              className="lg:col-span-3 bg-white rounded-2xl p-8 md:p-10 shadow-sm border border-gray-100"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={{
                hidden: { opacity: 0, y: 30 },
                visible: { opacity: 1, y: 0, transition: { duration: 0.6, delay: 0.2 } },
              }}
            >
              <h4 className="text-2xl font-bold text-zinc-900 mb-6">Nachricht senden</h4>

              {submissionState.succeeded ? (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex flex-col items-center justify-center gap-4 py-12 text-center"
                >
                  <div className="p-4 bg-primary/10 rounded-full">
                    <CheckCircle2 className="w-10 h-10 text-primary" />
                  </div>
                  <h5 className="text-xl font-bold text-zinc-900">Vielen Dank!</h5>
                  <p className="text-zinc-500 max-w-xs">
                    Ihre Nachricht ist bei uns eingegangen. Wir melden uns so schnell wie möglich bei Ihnen.
                  </p>
                </motion.div>
              ) : (
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-5">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                      <FormField
                        control={form.control}
                        name="name"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-zinc-700 font-medium">Name *</FormLabel>
                            <FormControl>
                              <Input placeholder="Max Mustermann" autoComplete="name" className="bg-gray-50 border-gray-200 focus:bg-white" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="email"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-zinc-700 font-medium">E-Mail *</FormLabel>
                            <FormControl>
                              <Input placeholder="mail@beispiel.de" type="email" autoComplete="email" className="bg-gray-50 border-gray-200 focus:bg-white" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="telefon"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-zinc-700 font-medium">Telefon (Optional)</FormLabel>
                            <FormControl>
                              <Input placeholder="Für einen schnellen Rückruf" type="tel" autoComplete="tel" className="bg-gray-50 border-gray-200 focus:bg-white" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="strasse"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-zinc-700 font-medium">Straße (Optional)</FormLabel>
                            <FormControl>
                              <Input placeholder="Straße und Hausnummer" autoComplete="street-address" className="bg-gray-50 border-gray-200 focus:bg-white" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="plz"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-zinc-700 font-medium">Postleitzahl (Optional)</FormLabel>
                            <FormControl>
                              <Input placeholder="PLZ" type="text" inputMode="numeric" autoComplete="postal-code" className="bg-gray-50 border-gray-200 focus:bg-white" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                      <FormField
                        control={form.control}
                        name="ort"
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-zinc-700 font-medium">Ort (Optional)</FormLabel>
                            <FormControl>
                              <Input placeholder="Ort" autoComplete="address-level2" className="bg-gray-50 border-gray-200 focus:bg-white" {...field} />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="message"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel className="text-zinc-700 font-medium">Ihre Nachricht *</FormLabel>
                          <FormControl>
                            <Textarea
                              placeholder="Wie können wir Ihnen helfen?"
                              className="min-h-[130px] bg-gray-50 border-gray-200 focus:bg-white resize-y"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    {submissionState.hasError && (
                      <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                        <AlertCircle className="w-4 h-4 shrink-0" />
                        Es ist ein Fehler aufgetreten. Bitte versuchen Sie es erneut oder kontaktieren Sie uns telefonisch.
                      </div>
                    )}

                    <Button
                      type="submit"
                      className="w-full h-12 text-base font-semibold"
                      disabled={submissionState.submitting}
                    >
                      {submissionState.submitting ? "Wird gesendet…" : "Nachricht absenden"}
                    </Button>
                    <p className="text-xs leading-relaxed text-zinc-500">
                      Mit dem Absenden des Formulars werden Ihre Angaben zur Bearbeitung Ihrer Anfrage verarbeitet.
                      Die Übermittlung erfolgt über unseren Formular-Dienstleister Formspree. Weitere Informationen
                      finden Sie in unserer <Link href="/datenschutz" className="text-primary hover:underline">Datenschutzerklärung</Link>.
                    </p>
                  </form>
                </Form>
              )}
            </motion.div>
          </div>
        </div>
      </section>

      {/* Google Maps Section */}
      <section className="bg-white">
        <div className="container mx-auto px-4 md:px-6 pb-0">
          <div className="flex items-center gap-2 py-8">
            <MapPin className="w-5 h-5 text-primary" />
            <span className="font-semibold text-zinc-700">Eichklingenweg 7, 74613 Öhringen - Eckartsweiler</span>
          </div>
        </div>
        <div className="w-full h-72 md:h-96">
          <GoogleMapConsent />
        </div>
      </section>
    </>
  );
}
